import {
  Component,
  Inject,
  Injector,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import {
  GridDataResult,
  DataStateChangeEvent,
} from '@progress/kendo-angular-grid';
import {
  MessageService,
  LoaderService,
  GridState,
  GridStateCompositeFilterDescriptor,
  GridRowType,
  DataGridComponent,
  ValidationResultService,
} from '@shared';
import { CategoryService, IChangeset } from '@shared-data';
import { ChangesetComponent } from '..';
import { RestaurantItemService } from '../../services';
import { CHANGESET_ROW, IAvailabilityItem } from '../../models';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { newFeatures } from '../../constants';

@Component({
  selector: 'app-base-availability-changeset',
  templateUrl: './base-availability-changeset.component.html',
  styleUrl: './base-availability-changeset.component.scss',
})
export class BaseAvailabilityChangesetComponent
  extends ChangesetComponent
  implements OnDestroy
{
  @ViewChild(DataGridComponent) grid!: DataGridComponent;

  private machineFilter: string | null = null;

  private routeParamSubscription!: Subscription;

  digitalAvailabilityEnabled?: boolean = true;

  // Confirmation dialog state
  showConfirmDialog = false;
  confirmDialogMessage = '';
  private confirmDialogCallback: (() => void) | null = null;

  newFeatures = newFeatures;

  constructor(
    validationErrorService: ValidationResultService,
    messageService: MessageService,
    loaderService: LoaderService,
    restaurantItemService: RestaurantItemService,
    categoryService: CategoryService,
    private activatedRoute: ActivatedRoute,
    injector: Injector,
    @Inject(CHANGESET_ROW)
    entityType: new (
      ...args: any[]
    ) => GridRowType & IChangeset & IAvailabilityItem,
  ) {
    super(
      validationErrorService,
      messageService,
      restaurantItemService,
      categoryService,
      loaderService,
      injector,
      entityType,
    );
    this.type = 'availability';

    this.subscribeToRouteParams();
  }

  subscribeToRouteParams(): void {
    this.routeParamSubscription = combineLatest([
      this.activatedRoute.params,
      this.activatedRoute.queryParams,
    ]).subscribe(([params, queryParams]) => {
      const routeSnapshot = this.activatedRoute.snapshot;
      this.productTypeId =
        routeSnapshot.url[routeSnapshot.url.length - 1]?.path;
      this.restaurantIds = queryParams['restaurantIds'].split(',') ?? [];
      this.restaurantId = queryParams['restaurantId'] ?? null;
      this.category = queryParams['category'] ?? null;
      this.machineFilter = queryParams['machine'] ?? null;
      
      this.handleFeatureVisibility(); 
      
      this.setDefaultState();
    });
  }

  private getMachineFilterDescriptor(
    machine: string,
  ): GridStateCompositeFilterDescriptor | null {
    if (machine !== null) {
      return new GridStateCompositeFilterDescriptor().and({
        field: 'dependencies',
        operator: 'contains',
        value: `|${machine}|`,
      });
    }
    return null;
  }

  handleFeatureVisibility() {
    const prevDigitalAvailabilityEnabled = this.digitalAvailabilityEnabled;
    
    this.digitalAvailabilityEnabled = environment.digitalAvailabilityRestaurants == null || this.restaurantIds?.every(id => (environment.digitalAvailabilityRestaurants ?? ['']).includes(id));
    const digitalAvailabilityColumns=this.columns.filter(col => col.field.toLowerCase().includes('deliveryavailability'));
    digitalAvailabilityColumns.forEach(col => {
      col.hidden = !this.digitalAvailabilityEnabled;
    });

    // clear cache if the feature flag changed and there are digital availability columns pending save
    if (this.digitalAvailabilityEnabled !== prevDigitalAvailabilityEnabled &&
      this.changeset.data.some((row: any) => digitalAvailabilityColumns.some(col => row[col.field] !== undefined 
        && row[col.field] !== row['originalValues'].get(col.field)))
    ) {
      this.cache.clear();
      this.clearDirtyRows();
    }
  }

  override populateGrid(gridDataResult: GridDataResult): void {

    const mainData = gridDataResult.data.filter(
      (row) => row.restaurantId == this.restaurantId,
    );
    const otherData = gridDataResult.data.filter(
      (row) => row.restaurantId != this.restaurantId,
    );

    gridDataResult.data = mainData.map((row: any) => {
      const gridRow = new this.entityType!(row) as IAvailabilityItem &
        GridRowType &
        IChangeset;

      const itemOtherData = otherData.filter(
        (r) => r.parItemId === row.parItemId,
      );

      // there is a pending change
      if (
        gridRow.newAvailability != gridRow.isActive ||
        itemOtherData.some((r) => r.newAvailability != null)
      ) {
        //gridRow.isActive = itemOtherData.every(r => r.isActive == row.isActive) ? gridRow.isActive : null;
        gridRow.newAvailability = itemOtherData.every(
          (r) => r.newAvailability == row.newAvailability,
        )
          ? gridRow.newAvailability
          : null;
      } else {
        //gridRow.isActive = itemOtherData.every(r => r.isActive == row.isActive) ? gridRow.isActive : null;
        gridRow.newAvailability = itemOtherData.every(
          (r) => r.isActive == row.isActive,
        )
          ? gridRow.newAvailability
          : null;
      }

      // there is a pending change
      if (
        gridRow.newDeliveryAvailability1P != gridRow.deliveryAvailability1P ||
        itemOtherData.some((r) => r.newDeliveryAvailability1P != null)
      ) {
        //gridRow.isActive = itemOtherData.every(r => r.isActive == row.isActive) ? gridRow.isActive : null;
        gridRow.newDeliveryAvailability1P = itemOtherData.every(
          (r) => r.newDeliveryAvailability1P == row.newDeliveryAvailability1P,
        )
          ? gridRow.newDeliveryAvailability1P
          : null;
      } else {
        //gridRow.isActive = itemOtherData.every(r => r.isActive == row.isActive) ? gridRow.isActive : null;
        gridRow.newDeliveryAvailability1P = itemOtherData.every(
          (r) => r.deliveryAvailability1P == row.deliveryAvailability1P,
        )
          ? gridRow.newDeliveryAvailability1P
          : null;
      }

      // there is a pending change
      if (
        gridRow.newDeliveryAvailability3P != gridRow.deliveryAvailability3P ||
        itemOtherData.some((r) => r.newDeliveryAvailability3P != null)
      ) {
        //gridRow.isActive = itemOtherData.every(r => r.isActive == row.isActive) ? gridRow.isActive : null;
        gridRow.newDeliveryAvailability3P = itemOtherData.every(
          (r) => r.newDeliveryAvailability3P == row.newDeliveryAvailability3P,
        )
          ? gridRow.newDeliveryAvailability3P
          : null;
      } else {
        //gridRow.isActive = itemOtherData.every(r => r.isActive == row.isActive) ? gridRow.isActive : null;
        gridRow.newDeliveryAvailability3P = itemOtherData.every(
          (r) => r.deliveryAvailability3P == row.deliveryAvailability3P,
        )
          ? gridRow.newDeliveryAvailability3P
          : null;
      }

      gridRow.originalValues.set('newAvailability', gridRow.newAvailability);
      gridRow.originalValues.set(
        'newDeliveryAvailability1P',
        gridRow.newDeliveryAvailability1P,
      );
      gridRow.originalValues.set(
        'newDeliveryAvailability3P',
        gridRow.newDeliveryAvailability3P,
      );

      gridRow.locked =
        row.availabilityScheduledDate != null ||
        row.pricingScheduledDate != null ||
        itemOtherData.some(
          (r) =>
            r.availabilityScheduledDate != null ||
            r.pricingScheduledDate != null,
        );

      return gridRow;
    });

    this.changeset = gridDataResult;
  }

  override fetchChangesets(event: GridState | DataStateChangeEvent): void {
    // Apply machine filter if present
    if (this.machineFilter) {
      const machineFilterDescriptor = this.getMachineFilterDescriptor(
        this.machineFilter,
      );

      if (machineFilterDescriptor) {
        if (!event.filter) {
          event.filter = machineFilterDescriptor;
        } else {
          event.filter = new GridStateCompositeFilterDescriptor(
            event.filter,
          ).and(machineFilterDescriptor);
        }
      }
    }

    // Apply availability-specific filter transformations
    if (event.filter) {
      event.filter = new GridStateCompositeFilterDescriptor(
        GridState.removeFilterByFields(
          event.filter,
          'isActive',
          'deliveryAvailability1P',
          'deliveryAvailability3P',
        ) ?? new GridStateCompositeFilterDescriptor(),
      );

      event.filter = GridState.substituteFilterField(
        event.filter,
        'newAvailability',
        'isActive',
        'newAvailability',
      );

      event.filter = GridState.substituteFilterField(
        event.filter,
        'newDeliveryAvailability1P',
        'deliveryAvailability1P',
        'newDeliveryAvailability1P',
      );

      event.filter = GridState.substituteFilterField(
        event.filter,
        'newDeliveryAvailability3P',
        'deliveryAvailability3P',
        'newDeliveryAvailability3P',
      );
    }

    super.fetchChangesets(event);
  }

  saveBulkFilteredChanges(applyToRestaurants: string[], enabled: boolean) {
    // Ensure we have filters applied
    if (
      !this.state.filter ||
      !this.state.filter.filters ||
      this.state.filter.filters.length === 0
    ) {
      this.messageService.error(
        'Please apply at least one filter before using this feature',
      );
      return;
    }

    const action = enabled ? 'on' : 'off';
    const restaurantCount = applyToRestaurants.length;

    this.confirmDialogMessage = `This will turn ${action} ${this.changeset.total} item(s) across ${restaurantCount} restaurant(s).`;

    this.confirmDialogCallback = () => {
      if (this.cache.size > 0) {
        this.cache.clear();
        this.clearDirtyRows();
      }
      this.executeBulkFilteredChanges(enabled);
    };

    this.showConfirmDialog = true;
  }

  private executeBulkFilteredChanges(enabled: boolean) {
    this.loaderService.show();

    // Create a state to fetch ALL filtered results (no pagination)
    const allResultsState = new GridState(
      999999, // Very large page size to get all results
      this.state.sort ?? this.initialSort,
      this.state.restaurantId,
      this.state.productType,
      this.state.restaurantIds,
    );
    // Copy the current filter
    allResultsState.filter = this.state.filter;
    allResultsState.skip = 0;

    this.state = allResultsState;
    // Fetch all changesets matching the filters
    this.service.getChangesets(this.type, allResultsState).subscribe({
      next: (gridDataResult) => {
        this.populateGrid(gridDataResult);

        if (gridDataResult.total === 0) {
          this.messageService.error('No items match the current filters');
          this.loaderService.hide();
          return;
        }

        // Update all filtered items
        gridDataResult.data = gridDataResult.data.map((item: any) => {
          item.newAvailability = enabled;
          //set the delivery availability values
          // TODO: re-enable when delivery availability is supported
          // item.newDeliveryAvailability1P = enabled;
          if (this.digitalAvailabilityEnabled) {
            item.newDeliveryAvailability3P = enabled;
          }

          return item;
        });

        gridDataResult.data.forEach((item) => {
          this.grid.onValueChange(item, 'newAvailability');
          // TODO: re-enable when delivery availability is supported
          //this.grid.onValueChange(item, 'newDeliveryAvailability1P');
          if (this.digitalAvailabilityEnabled) {
            this.grid.onValueChange(item, 'newDeliveryAvailability3P');
          }
        });

        this.loaderService.hide();
      },

      error: (err) => {
        this.messageService.error('Failed to load filtered items');
        const validationError = err?.error;
        if (validationError) {
          this.validationErrorService.show(validationError);
        }
        this.loaderService.hide();
      },
    });
  }

  onConfirmDialogClose() {
    this.showConfirmDialog = false;
    this.confirmDialogCallback = null;
  }

  onConfirmDialogProceed() {
    this.showConfirmDialog = false;
    if (this.confirmDialogCallback) {
      this.confirmDialogCallback();
      this.confirmDialogCallback = null;
    }
  }

  ngOnDestroy(): void {
    this.cache.clear();
    if (this.routeParamSubscription) {
      this.routeParamSubscription.unsubscribe();
    }
  }

  onValueChange(dataItem: { [key: string]: any } & GridRowType, field: string) {
    this.grid.onValueChange(dataItem, field);
  }
}
